import { useState, useEffect, useCallback } from "react";
import { axiosInstance } from "../../App";
import { BookOpen, Clock, CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

const REFRESH_INTERVAL = 60000; // 1 minute

export default function TemaCountdownWidget({ user, selectedBranch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChildren, setShowChildren] = useState(false);
  const [localTime, setLocalTime] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedBranch && selectedBranch !== "all") {
        params.append("branch", selectedBranch);
      }
      const url = `/dashboard/tema-countdown${params.toString() ? `?${params}` : ""}`;
      const res = await axiosInstance.get(url);
      setData(res.data);
      if (res.data.current_time_myt) {
        const [h, m] = res.data.current_time_myt.split(":").map(Number);
        setLocalTime({ hour: h, minute: m });
      }
    } catch (err) {
      console.error("Error fetching tema countdown:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedBranch]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Live countdown ticker
  useEffect(() => {
    if (!data?.has_tema || !localTime) return;
    const ticker = setInterval(() => {
      setLocalTime(prev => {
        if (!prev) return prev;
        let newMin = prev.minute + 1;
        let newHour = prev.hour;
        if (newMin >= 60) { newMin = 0; newHour++; }
        return { hour: newHour, minute: newMin };
      });
    }, 60000);
    return () => clearInterval(ticker);
  }, [data?.has_tema, localTime]);

  if (loading) return null;
  if (!data?.has_tema) return null;

  const { tema, countdown, tasks, children_not_updated, children_not_updated_count } = data;
  const phase = countdown?.phase || "before";

  // Recalculate remaining from localTime
  let remaining = countdown?.remaining_minutes_total || 0;
  let progress = countdown?.progress_percent || 0;
  if (localTime) {
    const currentMins = localTime.hour * 60 + localTime.minute;
    const endMins = (countdown?.end_hour || 15) * 60;
    const startMins = (countdown?.start_hour || 12) * 60;
    const total = endMins - startMins;
    if (currentMins >= endMins) {
      remaining = 0;
      progress = 100;
    } else if (currentMins >= startMins) {
      remaining = endMins - currentMins;
      progress = Math.round(((total - remaining) / total) * 100);
    } else {
      remaining = endMins - currentMins;
      progress = 0;
    }
  }
  const remHours = Math.floor(remaining / 60);
  const remMins = remaining % 60;

  const completedTasks = tasks?.completed || 0;
  const totalTasks = tasks?.total || 0;
  const allDone = completedTasks === totalTasks && totalTasks > 0;

  // Phase-based color scheme
  const phaseColors = {
    before: { bg: "from-indigo-500 to-blue-600", light: "bg-indigo-50 border-indigo-200", text: "text-indigo-700", accent: "text-indigo-500" },
    active: allDone
      ? { bg: "from-emerald-500 to-green-600", light: "bg-emerald-50 border-emerald-200", text: "text-emerald-700", accent: "text-emerald-500" }
      : { bg: "from-amber-500 to-orange-600", light: "bg-amber-50 border-amber-200", text: "text-amber-700", accent: "text-amber-500" },
    ended: allDone
      ? { bg: "from-emerald-500 to-green-600", light: "bg-emerald-50 border-emerald-200", text: "text-emerald-700", accent: "text-emerald-500" }
      : { bg: "from-red-500 to-rose-600", light: "bg-red-50 border-red-200", text: "text-red-700", accent: "text-red-500" }
  };
  const colors = phaseColors[phase];

  return (
    <div data-testid="tema-countdown-widget" className="mb-6">
      <div className={`relative overflow-hidden rounded-3xl shadow-xl border-2 ${colors.light}`}>
        {/* Header gradient bar */}
        <div className={`bg-gradient-to-r ${colors.bg} px-5 py-4`}>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-black text-base" data-testid="tema-title">
                {tema?.title || "Tema Hari Ini"}
              </h3>
              <p className="text-white/80 text-xs font-semibold">
                {new Date(data.date).toLocaleDateString("ms-MY", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </p>
            </div>
            {phase === "active" && (
              <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-xl px-3 py-1.5">
                <Clock className="w-4 h-4 text-white animate-pulse" />
                <span className="text-white font-black text-sm" data-testid="countdown-timer">
                  {remHours}j {remMins}m
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="p-5 bg-white">
          {/* Phase: Before 12 PM */}
          {phase === "before" && (
            <div className="text-center py-3">
              <div className="inline-flex items-center gap-2 bg-indigo-50 rounded-2xl px-5 py-3 mb-3">
                <Clock className="w-5 h-5 text-indigo-500" />
                <span className="text-indigo-700 font-bold text-sm">Countdown bermula pada 12:00 PM</span>
              </div>
              <p className="text-gray-500 text-sm font-medium">Sila rancang aktiviti mengikut tema hari ini</p>
            </div>
          )}

          {/* Phase: Active (12-3PM) or Ended */}
          {(phase === "active" || phase === "ended") && (
            <>
              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className={`text-xs font-bold ${colors.text}`}>
                    {phase === "ended" ? "Masa tamat!" : `Masa berbaki: ${remHours} jam ${remMins} minit`}
                  </span>
                  <span className={`text-xs font-bold ${colors.accent}`}>
                    {completedTasks}/{totalTasks} tugasan
                  </span>
                </div>
                <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden" data-testid="countdown-progress">
                  <div
                    className={`h-full rounded-full bg-gradient-to-r ${colors.bg} transition-all duration-1000`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {/* Task list */}
              <div className="space-y-2 mb-4" data-testid="task-list">
                {tasks?.items?.map((task, idx) => (
                  <div key={idx} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl ${task.done ? "bg-emerald-50" : "bg-red-50"}`}>
                    {task.done
                      ? <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      : <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />}
                    <span className={`text-sm font-semibold flex-1 ${task.done ? "text-emerald-700 line-through" : "text-red-700"}`}>
                      {task.label}
                    </span>
                  </div>
                ))}
              </div>

              {/* Children not updated */}
              {children_not_updated_count > 0 && (
                <div className="border border-orange-200 rounded-2xl overflow-hidden">
                  <button
                    data-testid="toggle-children-list"
                    onClick={() => setShowChildren(!showChildren)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-orange-50 hover:bg-orange-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-orange-500" />
                      <span className="text-sm font-bold text-orange-700">
                        {children_not_updated_count} kanak-kanak belum dikemaskini
                      </span>
                    </div>
                    {showChildren ? <ChevronUp className="w-4 h-4 text-orange-400" /> : <ChevronDown className="w-4 h-4 text-orange-400" />}
                  </button>
                  {showChildren && (
                    <div className="px-4 py-3 max-h-48 overflow-y-auto space-y-1.5" data-testid="children-not-updated-list">
                      {children_not_updated?.map((child, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
                          <span className="font-medium text-gray-700">{child.name}</span>
                          <span className="text-gray-400 text-xs">({child.class_name})</span>
                        </div>
                      ))}
                      {children_not_updated_count > 20 && (
                        <p className="text-xs text-gray-400 italic pt-1">
                          ... dan {children_not_updated_count - 20} lagi
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* All done message */}
              {allDone && (
                <div className="text-center py-3 bg-emerald-50 rounded-2xl mt-3">
                  <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-1" />
                  <p className="text-emerald-700 font-bold text-sm">Semua tugasan selesai!</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
